import { useMemo } from "react"
import { useParams } from "react-router-dom"

import { App, Form } from "@/atoms"
import { VALUES_PARAMS } from "@/const/routes.constants"
import { useCustomFunctionsForm } from "@/hooks/useCustomFunctionsForm"
import { usePrompt } from "@/hooks/usePrompt"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { FormContext, OnMessageProps } from "@/organisms/Form/hooks/useForm"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"

import type { FormProps } from "."

export const FormPage = (props: FormProps) => {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const custom = useCustomFunctionsForm()
  const { id: needle } = useParams()
  const isNewEntity = needle === VALUES_PARAMS.NEW_ENTITY
  const { id: formId, ...rest } = props
  const { t } = useTranslation()
  const currentAction = isNewEntity ? "created" : "updated"
  const { hasPermission } = useUser()
  const writeActionAllowed = !!hasPermission({
    resource: props.resource || "*",
    action: props.resource ? "write" : "*"
  })
  const messageKeyPrefix = "forms.messages"

  const value = useMemo(
    () => ({
      form,
      formId,
      custom,
      isNewEntity,
      entityId: isNewEntity ? undefined : needle,
      permissions: { write: writeActionAllowed },
      message: {
        instance: message,
        onSuccess: (name: string, { action = currentAction } = {}) =>
          message.success(t(`${messageKeyPrefix}.${action}`, { name })),
        onFailed: (
          _: string,
          { error, action = currentAction }: OnMessageProps = {}
        ) => {
          const detail = error?.data?.detail

          let errorMessage = t(`${messageKeyPrefix}.failed`, {
            action: t(`${messageKeyPrefix}.failedActions.${action}`, "")
          })

          if (typeof detail === "string" && !!detail) errorMessage = detail

          message.error(errorMessage)
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [custom, isNewEntity, needle, writeActionAllowed]
  )

  usePrompt(t("prompt.form.message"), custom.isDirty)

  if (rest.Component) {
    const { Component, children, ...restProps } = rest

    return (
      <FormContext.Provider value={value}>
        <Component {...restProps}>{children}</Component>
      </FormContext.Provider>
    )
  }

  const { Component, children, ...restProps } = rest

  return (
    <FormContext.Provider value={value}>
      <PageWithBreadcrumbTemplate {...restProps}>
        {children}
      </PageWithBreadcrumbTemplate>
    </FormContext.Provider>
  )
}
